// src/utils/api-helpers/UsersService.helpers.ts
// Helper functions and wrappers for UsersService
// NOTE: This file is NOT auto-generated and will NOT be overwritten by yarn generate:api
// It provides convenience methods that wrap or extend the generated UsersService

import { CancelablePromise } from '../../api/core/CancelablePromise';
import { OpenAPI } from '../../api/core/OpenAPI';
import { request as __request } from '../../api/core/request';
import type { UserFilter } from '../../types/user.types';
import { UsersService } from '../../api/services/UsersService';

export interface UserPermissionsResponse {
  permissions?: string[];
}

export class UsersServiceHelpers {
  /**
   * Find all users with filters and pagination
   * @param page Page number (1-based)
   * @param limit Number of items per page
   * @param filters Filter object
   * @returns Promise with users data
   */
  public static usersControllerFindAllWithFilters(
    page: number,
    limit: number,
    filters: UserFilter,
  ): CancelablePromise<any> {
    // Construir query params, removiendo valores undefined/null/vacíos
    const queryParams: Record<string, any> = {
      page,
      limit,
    };

    // Agregar searchTerm solo si tiene valor
    if (filters.searchTerm && filters.searchTerm.trim().length > 0) {
      queryParams.searchTerm = filters.searchTerm.trim();
    }

    // Agregar status solo si no es 'all'
    if (filters.status && filters.status !== 'all') {
      queryParams.status = filters.status;
    }

    // Agregar roleIds solo si hay roles seleccionados
    if (filters.roleIds && filters.roleIds.length > 0) {
      queryParams.roleIds = filters.roleIds;
    }

    // Agregar fechas solo si tienen valor
    if (filters.startDate) {
      queryParams.startDate = filters.startDate;
    }

    if (filters.endDate) {
      queryParams.endDate = filters.endDate;
    }

    // Agregar sortBy solo si tiene valor
    if (filters.sortBy) {
      queryParams.sortBy = filters.sortBy;
    }

    return __request(OpenAPI, {
      method: 'GET',
      url: '/users',
      query: queryParams,
    });
  }

  /**
   * Block a user
   * Uses the generated UsersService method
   */
  public static usersControllerBlock(
    id: string,
    requestBody?: { reason?: string },
  ): CancelablePromise<any> {
    // The generated method doesn't accept body, so we use direct request for now
    // TODO: Update backend to accept body parameter
    return __request(OpenAPI, {
      method: 'PUT',
      url: `/users/${id}/block`,
      body: requestBody || {},
      mediaType: 'application/json',
    });
  }

  /**
   * Unblock a user
   * Uses the generated UsersService method
   */
  public static usersControllerUnblock(id: string): CancelablePromise<any> {
    return UsersService.usersControllerUnblockUser(id);
  }

  /**
   * Export users to CSV
   * Uses the generated UsersService method
   */
  public static usersControllerExportCSV(): CancelablePromise<any> {
    return UsersService.usersControllerExportUsers();
  }

  /**
   * Get current user permissions
   * Uses the generated UsersService method
   */
  public static usersControllerGetMyPermissions(): CancelablePromise<UserPermissionsResponse> {
    return UsersService.usersControllerGetMyPermissions() as CancelablePromise<UserPermissionsResponse>;
  }

  /**
   * Get user permissions
   * Uses the generated UsersService method
   */
  public static usersControllerGetUserPermissions(
    id: string,
  ): CancelablePromise<UserPermissionsResponse> {
    return UsersService.usersControllerGetUserPermissions(
      id,
    ) as CancelablePromise<UserPermissionsResponse>;
  }

  /**
   * Reset user password (admin action)
   * Generates a temporary password and requires the user to change it on next login
   */
  public static resetPassword(
    id: string,
  ): CancelablePromise<{ temporaryPassword: string; expiresAt: string }> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: `/users/${id}/reset-password`,
    });
  }

  /**
   * Change own password (voluntary, requires current password)
   */
  public static changePassword(
    currentPassword: string,
    newPassword: string,
  ): CancelablePromise<{ message: string }> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/auth/change-password',
      body: { currentPassword, newPassword },
      mediaType: 'application/json',
    });
  }

  /**
   * Forced password change after admin reset (no current password needed)
   */
  public static forceChangePassword(newPassword: string): CancelablePromise<{ message: string }> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/auth/force-change-password',
      body: { newPassword },
      mediaType: 'application/json',
    });
  }
}
